import { clsx } from 'clsx';
import { useLazyLoadQuery } from 'react-relay';
import { NavLink } from 'react-router-dom';

import { t } from '../i18n/t';
import { MeQuery } from './guards';
import { LearnerPicture } from './LearnerPicture';
import { Logo } from './Logo';
import { useGuardedGo } from './useGuardedGo';

import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';

/**
 * The logo, and the learner's own picture as the way into their account: the
 * place most apps keep it, which frees the bottom bar for the five places a
 * learner goes to learn.
 *
 * `h-16` rather than padding, so the account link's hit area (44 px adult,
 * 64 px kid) fits the row without making it taller in either register. From
 * `md` up it spans the window, the logo over `NavRail` and the picture at
 * the far edge, as an app's chrome rather than the reading column's.
 */
export function AppHeader() {
  const go = useGuardedGo();
  const learner = useLazyLoadQuery<guardsMeQuery>(MeQuery, {}).me?.learner;

  return (
    <nav className="w-full shrink-0 pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex h-16 w-full max-w-2xl items-center justify-between px-4 md:max-w-none md:px-6">
        <Logo />
        {learner && (
          <NavLink
            to="/account"
            onClick={go('/account')}
            aria-label={t('nav-account')}
            className="flex size-(--hit-min) items-center justify-center rounded-full"
          >
            {({ isActive }) => (
              <span
                className={clsx(
                  'rounded-full ring-2 ring-offset-2 ring-offset-base-100 transition-shadow',
                  isActive ? 'ring-primary' : 'ring-transparent hover:ring-base-300',
                )}
              >
                <LearnerPicture
                  picture={learner.picture}
                  displayName={learner.displayName}
                  learnerId={learner.id}
                  size={32}
                />
              </span>
            )}
          </NavLink>
        )}
      </div>
    </nav>
  );
}
